/**
 * 服务端数据只放在 TanStack Query 里。查询键按资源分组，后端推来的变更事件据此失效对应缓存。
 * 改变状态只有一个入口：useAction（对应后端的动作网关）；阅读位置是例外（翻页就写，走专门的接口）。
 */
import { type QueryKey, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import "./client";
import {
  activity,
  annotations,
  approvals,
  capabilities,
  conversations,
  decide,
  jobs,
  models,
  position,
  runAction,
  savePosition,
  source,
  sources,
  sourceText,
} from "./gen/sdk.gen";
import type { ActionOutcome, PositionIn } from "./gen/types.gen";

export const keys = {
  sources: (status: string) => ["sources", status] as const,
  source: (id: string) => ["sources", "detail", id] as const,
  sourceText: (id: string) => ["sources", "text", id] as const,
  annotations: (id: string) => ["annotations", id] as const,
  position: (id: string) => ["position", id] as const,
  approvals: ["approvals"] as const,
  jobs: ["jobs"] as const,
  activity: ["activity"] as const,
  capabilities: ["capabilities"] as const,
  models: ["settings", "models"] as const,
  conversations: ["conversations"] as const,
};

/** 后端事件里的 resource → 需要失效的查询键前缀。 */
export const invalidation: Record<string, QueryKey[]> = {
  sources: [["sources"]],
  annotations: [["annotations"]],
  approvals: [["approvals"]],
  jobs: [["jobs"]],
  activity: [["activity"]],
  settings: [["settings"]],
  devices: [["devices"]],
  feedback: [["feedback"]],
};

export const useSources = (status = "active") =>
  useQuery({
    queryKey: keys.sources(status),
    queryFn: async () => (await sources({ query: { status } })).data,
  });

export const useSource = (id: string) =>
  useQuery({
    queryKey: keys.source(id),
    queryFn: async () => (await source({ path: { source_id: id } })).data,
  });

/** 解析出的文字（文字视图与 Markdown 用；EPUB / PDF 直接读原件）。 */
export const useSourceText = (id: string, enabled = true) =>
  useQuery({
    queryKey: keys.sourceText(id),
    queryFn: async () => (await sourceText({ path: { source_id: id } })).data,
    retry: false,
    enabled,
    staleTime: Infinity,
  });

export const useAnnotations = (id: string) =>
  useQuery({
    queryKey: keys.annotations(id),
    queryFn: async () => (await annotations({ path: { source_id: id } })).data,
  });

export const usePosition = (id: string) =>
  useQuery({
    queryKey: keys.position(id),
    queryFn: async () => (await position({ path: { source_id: id } })).data ?? null,
    staleTime: Infinity,
  });

/** 保存阅读位置（翻页时调用；调用方自己节流）。 */
export async function storePosition(id: string, body: PositionIn) {
  await savePosition({ path: { source_id: id }, body });
}

/** 最近的确认卡（含已处理、已更新为新版本的）；需要“待确认”时自己按状态筛。 */
export const useApprovals = () =>
  useQuery({
    queryKey: keys.approvals,
    queryFn: async () => (await approvals({ query: { status: "all" } })).data,
  });

export const useJobs = () => useQuery({ queryKey: keys.jobs, queryFn: async () => (await jobs()).data });

export const useActivity = () =>
  useQuery({ queryKey: keys.activity, queryFn: async () => (await activity()).data });

export const useCapabilities = () =>
  useQuery({
    queryKey: keys.capabilities,
    queryFn: async () => (await capabilities()).data,
    staleTime: Infinity,
  });

export const useModels = () =>
  useQuery({ queryKey: keys.models, queryFn: async () => (await models()).data });

export const useConversations = () =>
  useQuery({ queryKey: keys.conversations, queryFn: async () => (await conversations()).data });

/** 执行一个动作（与对话工具、命令面板同一个后端入口）。 */
export function useAction<P extends Record<string, unknown> = Record<string, unknown>>(actionId: string) {
  return useMutation<ActionOutcome, Error, P & { confirm_hash?: string }>({
    mutationFn: async ({ confirm_hash, ...params }) =>
      (
        await runAction({
          path: { action_id: actionId },
          body: { params, confirm_hash: confirm_hash ?? null },
        })
      ).data,
  });
}

export function useDecide() {
  const qc = useQueryClient();
  return useMutation<ActionOutcome, Error, { id: string; approve: boolean; confirm_hash?: string }>({
    mutationFn: async ({ id, approve, confirm_hash }) =>
      (await decide({ path: { approval_id: id }, body: { approve, confirm_hash: confirm_hash ?? null } }))
        .data,
    onSettled: () => qc.invalidateQueries({ queryKey: keys.approvals }),
  });
}
